import { useLazyQuery, useMutation, useReadQuery, type QueryRef } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { MigrationConnectForm, MigrationConnectFailure, type IMigrationConnectForm } from 'src/components/Migration';
import { type ILegacyRepo } from 'src/graphql/schema';
import { MigrateLegacyRepoDocument } from 'src/graphql/actions/legacyRepos/migrateLegacyRepo.mutation';
import { MigrateLegacyReposDocument } from 'src/graphql/actions/legacyRepos/migrateLegacyRepos.mutation';
import { OauthReposConnDocument } from 'src/graphql/actions/repositories/oauthReposConn.query';
import { ClientError, getApiError } from 'src/utils/errors';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { useMigrations } from '../model';
import { refetchLegacyRepos } from '../api';

import type { IIntegrationsQuery } from 'src/graphql/actions/integration/integrations.query';

const MigrationsConnectContainer = ({ queryRef }: { queryRef: QueryRef<IIntegrationsQuery> }) => {
  const [activeStep, setActiveStep] = useState<'submit' | 'failure' | null>(null);
  const [currentFailedRepoIds, setCurrentFailedRepoIds] = useState<string[]>([]);
  const [showRepositoryField, setShowRepositoryField] = useState(false);

  const { data: integrationsData } = useReadQuery(queryRef);
  const { ui, legacy } = useMigrations();
  const [migrateSingleRepo] = useMutation(MigrateLegacyRepoDocument);
  const [migrateMultipleRepos] = useMutation(MigrateLegacyReposDocument);
  const [getOauthReposConn, { data: oauthReposData }] = useLazyQuery(OauthReposConnDocument);

  useEffect(() => {
    if (ui.isConnectModalOpened) setActiveStep('submit');
  }, [ui.isConnectModalOpened]);

  const singleLegacyItemPlatform = legacy.repos.find((repo) => repo.id === ui.selectedRepoId)?.platform ?? '';
  const multiLegacyItemsPlatforms =
    ui.selectedRepoIds?.map((id) => legacy.repos.find((repo) => repo.id === id)?.platform || '') ?? [];
  const repositoryOptions =
    oauthReposData?.oauthReposConn.repositories.map(({ id, fullName }) => ({
      value: id,
      label: fullName ?? '',
    })) ?? [];

  const getFilteredIntegrationOptions = (platforms: string[]) => {
    const set = new Set(platforms);
    const integrationOptions =
      integrationsData.integrations.nodes.map(({ id, provider, accountName }) => ({
        value: id,
        provider,
        label: accountName ?? '',
      })) ?? [];

    return integrationOptions.filter(({ provider }) => set.has(provider));
  };

  const onSubmitSingle = async ({ integrationId, oauthRepoId }: IMigrationConnectForm) => {
    try {
      const { data } = await migrateSingleRepo({
        variables: {
          integrationId,
          legacyRepoId: ui.selectedRepoId ?? '',
          ...(oauthRepoId && { oauthRepoId }),
        },
      });
      if (data?.migrateLegacyRepo?.success) {
        refetchLegacyRepos();
        onClose();
        toast.success('Assets migrated!', {
          description:
            'Your assets are now connected via OAuth and will appear in the Deposits tab of their corresponding Software Escrow.',
        });
      } else throw new ClientError(data?.migrateLegacyRepo?.errors?.[0]);
    } catch (error: unknown) {
      // Need to switch to oauth repos, if integrations fails to connect
      if (error instanceof Error) {
        if (error.message === "Couldn't find a matching repository") {
          setShowRepositoryField(true);
          getOauthReposConn({
            variables: {
              integrationId,
            },
          });
        } else getApiError(error, toast.error);
      }
    }
  };

  const onSubmitMultiple = async (integrationId: string) => {
    try {
      const { data } = await migrateMultipleRepos({
        variables: {
          integrationId,
          legacyRepoIds: ui.selectedRepoIds ?? [],
        },
      });
      if (data?.migrateLegacyRepos?.success) {
        refetchLegacyRepos();

        const migratedRepoIds = data.migrateLegacyRepos.migratedLegacyRepos.map(({ id }) => id);
        // Show success or failure state
        if (migratedRepoIds.length === ui.selectedRepoIds?.length) {
          onClose();
          toast.success('Assets migrated!', {
            description:
              'Your assets are now connected via OAuth and will appear in the Deposits tab of their corresponding Software Escrow.',
          });
        } else {
          const failedRepoIds = ui.selectedRepoIds?.filter((id) => !migratedRepoIds.includes(id));

          ui.removeSelection();
          setActiveStep('failure');
          setCurrentFailedRepoIds(failedRepoIds ?? []);
        }
      } else {
        const error = data?.migrateLegacyRepos?.errors?.[0];

        // If was selected only one migration, show the next type of message
        if (error === "Couldn't find matching repositories") {
          setActiveStep('failure');
          setCurrentFailedRepoIds(ui?.selectedRepoIds ?? []);
        } else throw new ClientError(error);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onSubmit = async ({ integrationId, oauthRepoId }: IMigrationConnectForm) => {
    if (ui.selection === 'single') {
      await onSubmitSingle({ integrationId, oauthRepoId });
    } else if (ui.selection === 'multiple') {
      await onSubmitMultiple(integrationId);
    }
  };

  const onClose = () => {
    ui.setInitial();
    setActiveStep(null);
    setCurrentFailedRepoIds([]);
    setShowRepositoryField(false);
  };

  const currentFailedRepoItems = currentFailedRepoIds.reduce(
    (acc, repoId) => {
      const repo = legacy.repos.find((repo) => repo.id === repoId);

      if (repo) {
        acc.push({
          id: repo.id,
          title: getFailedRepoName(repo),
        });
      }

      return acc;
    },
    [] as { id: string; title: string }[],
  );

  const stepMap = {
    submit: {
      component: MigrationConnectForm,
      props: {
        onSubmit,
        integrationOptions:
          ui.selection === 'single'
            ? getFilteredIntegrationOptions([singleLegacyItemPlatform])
            : getFilteredIntegrationOptions(multiLegacyItemsPlatforms),
        repositoryOptions,
        showRepositoryField,
      },
    },
    failure: {
      component: MigrationConnectFailure,
      props: {
        items: currentFailedRepoItems,
      },
    },
  };

  const getActiveStep = <TComponent extends React.ElementType, TProps extends Record<string, unknown>>(
    key: typeof activeStep,
  ): {
    component: TComponent;
    props: TProps;
  } => ({
    component: stepMap[key!].component as TComponent,
    props: stepMap[key!].props as unknown as TProps,
  });

  const ActiveStep = activeStep ? getActiveStep(activeStep).component : null;

  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={ui.isConnectModalOpened} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] gap-6">
        {ActiveStep && <ActiveStep {...getActiveStep(activeStep).props} />}
      </DialogContent>
    </Dialog>
  );
};

function getFailedRepoName(repo: ILegacyRepo) {
  return [repo.title, repo.softwareEscrow.depositor?.companyName, repo.softwareEscrow.beneficiary?.companyName]
    .filter(Boolean)
    .join(' - ');
}

export default MigrationsConnectContainer;
